import { Avatar, Box, Chip, Divider, Drawer, IconButton, Skeleton, Tooltip, Typography } from '@mui/material'
import { getInitials } from '../../../../utils'
import DOMPurify from 'dompurify'
import File from '../../../../icons/File'
import { trpc } from '../../../../core/trpc'
import { EmailById } from '../../../../types/procedureOutputs'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import AssociateOffer from '../../../../components/email/AssociateOffer'
import toast from 'react-hot-toast'
import { useQueryClient } from '@tanstack/react-query'
import EmailDrawerReply from './EmailDrawerReply'
import ReplyAllIcon from '@mui/icons-material/ReplyAll'
import ReplyIcon from '@mui/icons-material/Reply'
import ShortcutIcon from '@mui/icons-material/Shortcut'
import clsx from 'clsx'
import azEmailService from '../../../../services/azureGraphEmailService'
import { WarningAmberOutlinedIcon } from '../../../../assets/icons'
import { useEmailStore } from '../../../../stores/emailStore'
import { useSession } from 'next-auth/react'
import MSEmailFileViewer from '../../../../components/files/FileViewer/MSEmailFileViewer'
import { useStores } from '../../../../hooks/useStores'
import debounce from 'lodash.debounce'
import { observer } from 'mobx-react-lite'

type EmailDrawerProps = {
  emailId: string | undefined
  maxZindex?: boolean
  refetch?: ({ concat, activityType }: { concat: boolean; activityType: string | undefined }) => void

  onClose: () => void
}

const EmailDrawer = ({ emailId, onClose, maxZindex, refetch: refetchActivities }: EmailDrawerProps) => {
  DOMPurify.addHook('afterSanitizeAttributes', function (node) {
    // set all elements owning target to target=_blank
    if ('target' in node) {
      node.setAttribute('target', '_blank')
      node.setAttribute('rel', 'noopener')
    }
  })

  const { emailStore } = useStores()
  const navigate = useNavigate()
  const user = useSession().data?.user
  const { leadId } = useParams()
  const { mailboxEmailAddress: currentMailboxAddress } = useEmailStore()
  const [sendType, setSendType] = useState<'forward' | 'reply' | 'replyAll'>()
  const [attachmentsLoading, setAttachmentsLoading] = useState(false)
  const [contentLoading, setContentLoading] = useState(false)
  const [previewAttachmentId, setPreviewAttachmentId] = useState<string | null>()
  const { data } = trpc.emails.getEmailById.useQuery({ emailId: emailId || '' }, { enabled: Boolean(emailId) })
  const { data: lead, refetch } = trpc.lead.getLeadById.useQuery(
    {
      id: leadId || '',
      includeOffers: true
    },
    { enabled: Boolean(leadId) }
  )
  const client = useQueryClient()
  const [thisEmailIdInMyMessages, setThisEmailIdInMyMessages] = useState<string | null>(null)
  const [mailboxEmailWasFound, setMailboxEmailWasFound] = useState('')
  const emailIdInDifferentMailbox = mailboxEmailWasFound != currentMailboxAddress

  const previewAttachment = useMemo(() => {
    return (emailStore.email?.attachments ?? []).find((a) => a.id === previewAttachmentId)
  }, [previewAttachmentId])

  // const setMessageId = async () => {
  //   if (data?.internet_message_id) {
  //     getMyEmailByInternetMessageId(data?.internet_message_id, currentMailboxAddress).then((emails) => {
  //       if (emails?.data.value.length) {
  //         setThisEmailIdInMyMessages(emails?.data?.value?.[0]?.id)
  //       } else {
  //         const allAddresses = user?.email ? user?.shared_mailboxes.concat(user.email) : user?.shared_mailboxes
  //         const otherEmailAddresses = allAddresses?.filter((address) => address !== currentMailboxAddress)
  //         if (otherEmailAddresses?.length) {
  //           otherEmailAddresses.forEach((address) => {
  //             if (data.internet_message_id) {
  //               getMyEmailByInternetMessageId(data.internet_message_id, address).then((emails) => {
  //                 if (emails?.data.value.length) {
  //                   setThisEmailIdInMyMessages(emails?.data?.value?.[0]?.id)
  //                   setEmailIdInDifferentMailbox(true)
  //                   setSharedMailboxAddress(address)
  //                 }
  //               })
  //             }
  //           })
  //         }
  //       }
  //     })
  //   }
  // }

  const loadEmail = debounce(async () => {
    if (!data) return

    try {
      setContentLoading(true)
      setAttachmentsLoading(true)

      if (user) {
        console.debug('EmailDrawer.loadEmail', { currentMailboxAddress })
        console.debug('EmailDrawer.loadEmail.data', { data })

        const result = await azEmailService.findEmailByInternetMessageId(data!.internet_message_id as string, [
          currentMailboxAddress,
          ...(user!.shared_mailboxes ?? [])
        ])

        if (!result) {
          console.log('EmailDrawer email not found')
          return
        }

        console.log('EmailDrawer.loadEmail.result', result)

        const { mailbox, email } = result!
        console.debug('EmailDrawer.loadEmail', mailbox, email)
        setMailboxEmailWasFound(mailbox)
        setThisEmailIdInMyMessages(email.id)
        await emailStore.injectEmail(email, mailbox)
      }
    } catch (e) {
      toast.error('failed to load email or attachments')
      console.log(e)
    } finally {
      setContentLoading(false)
      setAttachmentsLoading(false)
    }

    // const email = await azEmailService.retrieveEmail(sharedMailboxAddress, data!.microsoft_id)
  }, 200)

  useEffect(() => {
    console.log('EmailDrawer loading email')
    loadEmail()
  }, [data?.microsoft_id, user, currentMailboxAddress])

  const handlePreviewFile = (id: string) => setPreviewAttachmentId(id)

  const associateOffer = trpc.offer.associateOfferToEmail.useMutation({
    onSuccess: () => {
      refetch()
      client.refetchQueries([['offer', 'getOffers'], { input: { leadId, skip: 0, take: 20 }, type: 'query' }])
    },
    onError: (e) => {
      toast.error(String(e))
    }
  })

  const unassociateOffer = trpc.offer.unassociateOfferToEmail.useMutation({
    onSuccess: () => {
      refetch()
      client.refetchQueries([['offer', 'getOffers'], { input: { leadId, skip: 0, take: 20 }, type: 'query' }])
    },
    onError: (e) => {
      toast.error(String(e))
    }
  })

  // const loadEmailAttachments = async () => {
  //   if (!thisEmailIdInMyMessages) return
  //
  //   setAttachmentsLoading(true)
  //
  //   const attachments = await azEmailService.listEmailAttachments({
  //     emailId: thisEmailIdInMyMessages,
  //     mailboxEmailAddress: sharedMailboxAddress
  //   })
  //
  //   setOriginalAttachments(attachments)
  //
  //   setAttachmentsLoading(false)
  // }

  const renderChips = (email: EmailById | undefined, options: 'to_addresses' | 'bcc_recipients' | 'cc_recipients') => {
    return email?.[options]?.map((recipient) => (
      <Tooltip
        key={(recipient as { emailAddress: { address: string } })?.emailAddress?.address}
        title={(recipient as { emailAddress: { address: string } })?.emailAddress?.address}
      >
        <Chip
          className="bg-white border-[1px] border-solid border-grey-300 shadow-sm"
          label={(recipient as { emailAddress: { address: string } })?.emailAddress?.address}
          avatar={
            <Avatar>
              {getInitials(
                (
                  recipient as {
                    emailAddress: { address: string }
                  }
                )?.emailAddress?.address
              )}
            </Avatar>
          }
        />
      </Tooltip>
    ))
  }

  const associatedOffers = lead?.offers.filter((offer) => offer.emails.find((email) => Boolean(email) && email.id === emailId))

  const handleClose = () => {
    setSendType(undefined)
    onClose()
  }

  return (
    <Drawer anchor="right" open={Boolean(emailId)} onClose={handleClose}>
      <Box className="w-[700px] flex flex-grow flex-col">
        <Box className="p-6 flex flex-col justify-between h-full">
          <Box className="flex flex-grow">
            <Box className="flex flex-grow flex-col">
              <Box className="flex justify-between items-center">
                <Box className="flex gap-8">
                  <Typography className="text-grey-600">From</Typography>
                  <Typography>
                    {(
                      data?.from_address as {
                        emailAddress: { address: string }
                      }
                    )?.emailAddress?.address || ''}
                  </Typography>
                </Box>
              </Box>
              <Divider className="my-3" />
              <Box className="flex gap-8">
                <Typography className="text-grey-600">To</Typography>
                <Box className="flex gap-2 flex-wrap">{renderChips(data, 'to_addresses')}</Box>
              </Box>
              <Divider className="my-3" />
              {Boolean(data?.cc_recipients.length) && (
                <>
                  <Box className="flex gap-8">
                    <Typography className="text-grey-600">Cc</Typography>
                    <Box className="flex gap-2 flex-wrap">{renderChips(data, 'cc_recipients')}</Box>
                  </Box>
                  <Divider className="my-3" />
                </>
              )}
              {Boolean(data?.bcc_recipients.length) && (
                <>
                  <Box className="flex gap-8">
                    <Typography className="text-grey-600">Bcc</Typography>
                    <Box className="flex gap-2 flex-wrap">{renderChips(data, 'bcc_recipients')}</Box>
                  </Box>
                  <Divider className="my-3" />
                </>
              )}
              <Typography>{data?.subject}</Typography>

              <Divider className="my-3" />

              {contentLoading && <Skeleton width={100} />}

              <div dangerouslySetInnerHTML={{ __html: emailStore.email?.contentBody ?? '' }} className="w-full" />

              {attachmentsLoading && <Skeleton width={175} height={40} />}

              {Boolean(emailStore.email?.hasAttachments) && (
                <Box className="flex gap-2 flex-wrap mt-3">
                  {emailStore.email?.attachments?.map((attachment) => (
                    <Tooltip title={attachment.name}>
                      <Box
                        onClick={() => handlePreviewFile(attachment.id)}
                        key={attachment.name}
                        className="px-2 py-1 w-[250px] cursor-pointer truncate flex justify-between items-center border-solid border-[1px] border-grey-200 rounded-full"
                      >
                        <Box className="flex gap-2.5 items-center">
                          <File fontSize="small" sx={{ color: '#697586' }} />
                          <Typography className="truncate max-w-[175px]">{attachment.name}</Typography>
                        </Box>
                      </Box>
                    </Tooltip>
                  ))}
                </Box>
              )}
              <Box className="flex gap-2 items-center justify-start">
                <Tooltip title="Reply">
                  <IconButton
                    disabled={(sendType && sendType !== 'reply') || !thisEmailIdInMyMessages}
                    className={clsx({ 'bg-grey-100': sendType === 'reply' })}
                    onClick={() => setSendType('reply')}
                  >
                    <ReplyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Reply all">
                  <IconButton
                    disabled={(sendType && sendType !== 'replyAll') || !thisEmailIdInMyMessages}
                    className={clsx({ 'bg-grey-100': sendType === 'replyAll' })}
                    onClick={() => setSendType('replyAll')}
                  >
                    <ReplyAllIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Forward">
                  <IconButton
                    disabled={(sendType && sendType !== 'forward') || !thisEmailIdInMyMessages}
                    className={clsx({ 'bg-grey-100': sendType === 'forward' })}
                    onClick={() => setSendType('forward')}
                  >
                    <ShortcutIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              {Boolean(thisEmailIdInMyMessages && emailIdInDifferentMailbox) && (
                <Typography className="text-warning-700 text_sm_medium">
                  <WarningAmberOutlinedIcon className="text-sm align-middle" />
                  You are replying/forwarding a message from a different shared mailbox.
                </Typography>
              )}
              {thisEmailIdInMyMessages === null && (
                <Typography className="text-warning-700 text_sm_medium">
                  <WarningAmberOutlinedIcon className="text-sm align-middle" />
                  You are only allowed to reply or forward an email that was sent to you.
                </Typography>
              )}
              {sendType && thisEmailIdInMyMessages && (
                <EmailDrawerReply
                  mailboxEmailAddress={currentMailboxAddress} // ={sharedMailboxAddress}
                  originalAttachments={emailStore.email?.attachments.map((a) => a.rawObject) ?? []}
                  currentUserEmailId={thisEmailIdInMyMessages}
                  refetch={refetchActivities}
                  leadId={leadId}
                  onClose={onClose}
                  emailBody={emailStore.email?.rawHTMLBody ?? ''}
                  sendType={sendType}
                  setSendType={setSendType}
                  email={data}
                />
              )}
            </Box>
          </Box>
          {Boolean(lead?._count.offers) && Number(lead?._count.offers) > Number(associatedOffers?.length) && (
            <AssociateOffer
              onChange={(offerId) => associateOffer.mutate({ offerId, emailId: emailId || '' })}
              leadId={leadId || ''}
              emailId={emailId || ''}
            />
          )}
          {Boolean(associatedOffers?.length) && (
            <Box className="flex gap-2 mt-2">
              {associatedOffers?.map((associatedOffer) => (
                <Chip
                  key={associatedOffer.id}
                  onClick={() => navigate(`/offers/${associatedOffer?.id}`)}
                  className="shadow-sm text-grey-600 bg-white border-solid border-[1px] border-grey-300 w-fit"
                  onDelete={() => unassociateOffer.mutate({ offerId: associatedOffer.id, emailId: emailId || '' })}
                  label={<Typography className="text_sm_normal text-grey-600">Offer - {associatedOffer?.funder.name}</Typography>}
                />
              ))}
            </Box>
          )}
        </Box>
      </Box>

      {Boolean(previewAttachment) && Boolean(thisEmailIdInMyMessages) && (
        <MSEmailFileViewer
          emailId={thisEmailIdInMyMessages!}
          attachment={previewAttachment!}
          open={Boolean(previewAttachment)}
          onClose={() => setPreviewAttachmentId(null)}
          attachmentsList={emailStore.email?.attachments ?? []}
          onOpenAttachmentFromList={handlePreviewFile}
        />
      )}
    </Drawer>
  )
}

export default observer(EmailDrawer)
